import { z } from "zod";
import { SupportedCurrencyCodesSchema } from "./currencies.js";
import { ItemSplitSchema, PaymentSchema } from "./expenses.js";
/**
 * Recurrence cadence units. Day and hour units are intentionally excluded:
 * the product floor is "no more frequent than weekly", which `WEEK` with
 * `intervalCount = 1` realises exactly.
 */
export const RecurrenceUnitSchema = z.enum(["WEEK", "MONTH", "YEAR"]);
/** ISO 8601 datetime that lands on the top of an hour in UTC. */
const HourlyAnchorSchema = z
    .string()
    .datetime()
    .refine((value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
        return false;
    return (d.getUTCMinutes() === 0 &&
        d.getUTCSeconds() === 0 &&
        d.getUTCMilliseconds() === 0);
}, {
    message: "Must be aligned to the top of an hour (UTC minutes/seconds 0)",
});
/**
 * Bounded interval. Combined with `unit`, this is the recurrence cadence.
 * Capped at 52 to prevent absurd schedules (e.g. "every 999 years") that
 * would lead to Date overflow in next-run arithmetic.
 */
const IntervalCountSchema = z.number().int().min(1).max(52);
/** A single recurring-expense item, mirroring the create-expense item shape. */
export const RecurringItemSchema = z.strictObject({
    label: z.string().min(1),
    /** Signed minor units; recurring discounts are negative. */
    cost: z.number().int(),
    split: ItemSplitSchema,
});
/**
 * The recurring expense template + its schedule, returned by the API.
 *
 * `nextRunAt` is the next UTC top-of-hour the tick lambda will materialise an
 * expense for. After a successful run, the service advances it by
 * `intervalCount × unit`. Clients render the next-run instant in the
 * viewer's local timezone — the wire shape is UTC only.
 */
export const RecurringExpenseSchema = z.strictObject({
    id: z.string().uuid(),
    groupId: z.string().uuid(),
    label: z.string().min(1).max(120),
    currency: SupportedCurrencyCodesSchema,
    payments: z.array(PaymentSchema).min(1),
    items: z.array(RecurringItemSchema).min(1),
    unit: RecurrenceUnitSchema,
    intervalCount: IntervalCountSchema,
    nextRunAt: z.string().datetime(),
    status: z.enum(["ACTIVE", "PAUSED"]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateRecurringExpenseRequestSchema = z.strictObject({
    label: z.string().min(1).max(120),
    currency: SupportedCurrencyCodesSchema,
    payments: z.array(PaymentSchema).min(1),
    items: z.array(RecurringItemSchema).min(1),
    unit: RecurrenceUnitSchema,
    intervalCount: IntervalCountSchema,
    nextRunAt: HourlyAnchorSchema,
});
export const UpdateRecurringExpenseRequestSchema = z.strictObject({
    label: z.string().min(1).max(120).optional(),
    payments: z.array(PaymentSchema).min(1).optional(),
    items: z.array(RecurringItemSchema).min(1).optional(),
    unit: RecurrenceUnitSchema.optional(),
    intervalCount: IntervalCountSchema.optional(),
    nextRunAt: HourlyAnchorSchema.optional(),
    status: z.enum(["ACTIVE", "PAUSED"]).optional(),
});
